import { Breadcrumb, Event } from '@sentry/react';

const REDACTED = '[REDACTED]';

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
const JWT_REGEX = /\b(eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+)\b/gi;
const AUTH_HEADER_REGEX = /(bearer\s+|token[=:]\s*|api[_-]?key[=:]\s*)[a-zA-Z0-9._~+/-]+=*/gi;

const PII_KEYS = new Set([
  'email',
  'firstname',
  'lastname',
  'first_name',
  'last_name',
  'name',
  'dob',
  'dateofbirth',
  'date_of_birth',
  'gender',
  'address',
  'billingaddress',
  'billing_address',
  'billing',
  'card',
  'payment',
  'cvv',
  'password',
  'token',
  'authtoken',
  'auth_token',
  'bookingid',
  'booking_id',
  'booking',
  'traveler',
  'travelers',
  'chat',
  'message',
  'phone',
  'phonenumber',
  'phone_number'
]);

const isPiiKey = (key: string): boolean => {
  const normalized = key.toLowerCase().replace(/[^a-z0-9_]/g, '');
  return PII_KEYS.has(normalized);
};

export const scrubString = (str: string): string => {
  if (!str) return str;
  let result = str;
  result = result.replace(EMAIL_REGEX, REDACTED);
  result = result.replace(JWT_REGEX, REDACTED);
  result = result.replace(AUTH_HEADER_REGEX, `$1${REDACTED}`);
  return result;
};

export const scrubUrl = (urlStr: string): string => {
  if (!urlStr) return urlStr;
  try {
    const isRelative = !urlStr.startsWith('http://') && !urlStr.startsWith('https://');
    const dummyBase = 'http://localhost';
    const url = isRelative ? new URL(urlStr, dummyBase) : new URL(urlStr);
    let changed = false;
    url.searchParams.forEach((value, key) => {
      if (isPiiKey(key) || EMAIL_REGEX.test(value) || JWT_REGEX.test(value)) {
        url.searchParams.set(key, REDACTED);
        changed = true;
      }
    });
    if (changed) {
      return isRelative ? url.pathname + url.search + url.hash : url.toString();
    }
    return scrubString(urlStr);
  } catch {
    return scrubString(urlStr);
  }
};

export const scrubValue = (value: any, visited = new WeakSet<object>()): any => {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object') {
    if (visited.has(value)) {
      return REDACTED;
    }
    visited.add(value);

    if (Array.isArray(value)) {
      return value.map(item => scrubValue(item, visited));
    }

    const scrubbedObj: Record<string, any> = {};
    for (const [key, val] of Object.entries(value)) {
      if (isPiiKey(key)) {
        scrubbedObj[key] = REDACTED;
      } else {
        scrubbedObj[key] = scrubValue(val, visited);
      }
    }
    return scrubbedObj;
  }
  return value;
};

export const setupConsoleScrubber = () => {
  if (typeof window === 'undefined') return;

  const consoleMethods: Array<keyof Console> = ['log', 'warn', 'error', 'info', 'debug'];

  consoleMethods.forEach(method => {
    const originalMethod = console[method];
    if (typeof originalMethod === 'function') {
      // @ts-ignore
      console[method] = (...args: any[]) => {
        try {
          const scrubbedArgs = args.map(arg => scrubValue(arg));
          originalMethod.apply(console, scrubbedArgs);
        } catch {
          originalMethod.apply(console, args);
        }
      };
    }
  });
};

export const beforeSendScrubber = (event: Event): Event | null => {
  if (!event) return event;

  // 1. Remove/scrub user context
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
    if (event.user.id) {
      event.user.id = REDACTED;
    }
  }

  // 2. Scrub error messages and exception values
  if (event.message) {
    event.message = scrubString(event.message);
  }

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map(ex => ({
      ...ex,
      value: ex.value ? scrubString(ex.value) : ex.value,
      type: ex.type ? scrubString(ex.type) : ex.type
    }));
  }

  // 3. Scrub request details (URL, query string, headers)
  if (event.request) {
    if (event.request.url) {
      event.request.url = scrubUrl(event.request.url);
    }
    if (event.request.queryString) {
      if (typeof event.request.queryString === 'string') {
        event.request.queryString = scrubString(event.request.queryString);
      } else {
        event.request.queryString = scrubValue(event.request.queryString);
      }
    }
    if (event.request.data) {
      event.request.data = scrubValue(event.request.data);
    }
  }

  // 4. Scrub extra, tags, contexts
  if (event.extra) {
    event.extra = scrubValue(event.extra);
  }
  if (event.tags) {
    event.tags = scrubValue(event.tags);
  }
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts);
  }

  // 5. Scrub breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(bc => beforeBreadcrumbScrubber(bc)).filter(Boolean) as Breadcrumb[];
  }

  return event;
};

export const beforeBreadcrumbScrubber = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (!breadcrumb) return breadcrumb;
  if (breadcrumb.message) {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubValue(breadcrumb.data);
  }
  return breadcrumb;
};
